/**
 * Integrytas Group - Common Security Module (CSM)
 * Change Password modal - Acumatica-style centered dialog.
 *
 * Replaces the previous `window.prompt()` call per v14 standard 2.6 which
 * requires the management-tool aesthetic for every interaction.
 *
 * Behavior:
 *   - Asks for the new password twice to catch typos.
 *   - Enforces a minimum length matching the Supabase default (8 chars).
 *   - Calls the parent onSubmit callback (typically the auth hook's
 *     changePassword) and renders any thrown error in-line.
 *   - On success shows a brief confirmation banner, then auto-closes.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (newPassword: string) => Promise<void>;
}

const MIN_PASSWORD_LENGTH = 8;

export function ChangePasswordModal({ open, onClose, onSubmit }: ChangePasswordModalProps) {
  const { t } = useTranslation();
  const [next, setNext]       = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [done, setDone]       = useState(false);

  if (!open) return null;

  const reset = () => {
    setNext(''); setConfirm(''); setError(null); setDone(false); setBusy(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (next.length < MIN_PASSWORD_LENGTH) {
      setError(t('auth.password_min', { count: MIN_PASSWORD_LENGTH }));
      return;
    }
    if (next !== confirm) {
      setError(t('auth.passwords_dont_match'));
      return;
    }

    setBusy(true);
    try {
      await onSubmit(next);
      setDone(true);
      setTimeout(handleClose, 1200);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm rounded border border-csm-border bg-white shadow-xl"
      >
        <header className="flex items-center justify-between border-b border-csm-border px-3 py-2">
          <h2 className="text-csm-md font-semibold text-csm-ink">{t('auth.change_password')}</h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label={t('common.close')}
            className="text-csm-muted hover:text-csm-ink"
          >x</button>
        </header>

        <div className="p-4">
          {done ? (
            <div className="rounded border border-csm-success-border bg-csm-success-soft px-2 py-2 text-csm-sm text-csm-success">
              {t('auth.password_updated')}
            </div>
          ) : (
            <>
              <label className="mb-1 block text-csm-sm font-medium text-csm-ink">{t('auth.new_password')}</label>
              <input
                type="password"
                value={next}
                onChange={e => setNext(e.target.value)}
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
              />

              <label className="mb-1 block text-csm-sm font-medium text-csm-ink">{t('auth.confirm_password')}</label>
              <input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
              />

              <p className="mb-3 text-csm-xs text-csm-muted">
                {t('common.min_length', { count: MIN_PASSWORD_LENGTH })}
              </p>

              {error && (
                <div className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <footer className="flex justify-end gap-2 border-t border-csm-border bg-csm-border-soft px-3 py-2">
          <button
            type="button"
            onClick={handleClose}
            className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft"
          >{t('common.cancel')}</button>
          {!done && (
            <button
              type="submit"
              disabled={busy}
              className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60"
            >
              {busy ? t('auth.updating') : t('auth.update_password')}
            </button>
          )}
        </footer>
      </form>
    </div>
  );
}
